import React from 'react';
import { AlertTriangle, CheckCircle2, Users } from 'lucide-react';
import Button from '../../common/Button/Button';
import CreditBar from '../../common/CreditBar/CreditBar';
import { UnlockEstimateResponse } from '../../../types/results.types';

interface EnrichmentConfirmScreenProps {
  estimate: UnlockEstimateResponse | null;
  estimateLoading: boolean;
  personCount: number;
  totalToUnlock: number | null;
  dmUnlocksRemaining: number | null;
  balanceLoading?: boolean;
  balanceError?: boolean;
  onRetryBalance?: () => void;
  canUnlock: boolean;
  estimateError: string | null;
  submitError: string | null;
  submitLoading: boolean;
  showCompanyBreakdown?: boolean;
  autoSelectedSubset?: boolean;
  onChangeSelection?: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  onUpgradeClick?: () => void;
  dmUnlocksUsed?: number;
  costHint?: string;
}

const Shimmer: React.FC<{ className?: string }> = ({ className = 'h-4 w-12' }) => (
  <span
    className={`inline-block animate-pulse rounded bg-gray-200 align-middle ${className}`}
    aria-hidden="true"
  />
);

const EnrichmentConfirmScreen: React.FC<EnrichmentConfirmScreenProps> = ({
  estimate,
  estimateLoading,
  personCount,
  totalToUnlock,
  dmUnlocksRemaining,
  balanceLoading = false,
  balanceError = false,
  onRetryBalance,
  canUnlock,
  estimateError,
  submitError,
  submitLoading,
  showCompanyBreakdown = true,
  autoSelectedSubset = false,
  onChangeSelection,
  onCancel,
  onConfirm,
  onUpgradeClick,
  dmUnlocksUsed = 0,
  costHint,
}) => {
  const remainingAfterSelection =
    dmUnlocksRemaining !== null && totalToUnlock !== null
      ? dmUnlocksRemaining - totalToUnlock
      : null;
  const insufficientCredits = remainingAfterSelection !== null && remainingAfterSelection < 0;
  const nothingToUnlock = !estimateLoading && totalToUnlock === 0;
  const isExhausted = !balanceLoading && !balanceError && dmUnlocksRemaining === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg bg-[#8A3033]/5 border border-[#8A3033]/15 px-4 py-3 flex items-start gap-3">
        <Users className="h-4 w-4 text-[#8A3033] shrink-0 mt-0.5" aria-hidden />
        <p className="text-sm text-gray-700">
          We'll find <span className="font-semibold">direct owner and decision maker emails</span>, not generic info@ addresses.
          You only use a lookup when we find an email. Looking is always free.
        </p>
      </div>

      {autoSelectedSubset && dmUnlocksRemaining !== null && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-sm text-amber-800">
          You have {dmUnlocksRemaining.toLocaleString()} credits. We have automatically selected the highest-rated businesses. You can adjust below if needed.
        </div>
      )}

      <CreditBar
        balance={dmUnlocksRemaining}
        balanceLoading={balanceLoading}
        balanceError={balanceError}
        onRetryBalance={onRetryBalance}
        overBudget={insufficientCredits}
        icon="people"
        unitLabel="people enrichments"
      >
        {totalToUnlock !== null && totalToUnlock > 0 && !estimateLoading && (
          <>
            <span className="text-gray-500">up to {totalToUnlock} credits</span>
            <span className="text-gray-300">·</span>
            <span className="flex items-center gap-1 font-medium">
              {remainingAfterSelection !== null ? (
                <>
                  <Users className={`h-3.5 w-3.5 ${insufficientCredits ? 'text-red-600' : 'text-purple-600'}`} aria-hidden />
                  <span className={`font-semibold ${insufficientCredits ? 'text-red-600' : 'text-purple-600'}`}>
                    {remainingAfterSelection.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-500">remaining after</span>
                </>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </span>
          </>
        )}
      </CreditBar>

      {isExhausted && onUpgradeClick && (
        <div className="flex flex-col gap-2 rounded-lg bg-[#8A3033]/5 border border-[#8A3033]/20 px-3.5 py-3 text-sm">
          <p className="text-gray-800">
            You found {dmUnlocksUsed.toLocaleString()} decision maker emails. Upgrade to Starter to keep finding owner emails.
          </p>
          <Button onClick={onUpgradeClick} variant="primary" className="w-full sm:w-auto sm:self-start">
            Upgrade to Starter
          </Button>
        </div>
      )}

      {insufficientCredits && !isExhausted && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Not enough credits. Reduce selection or upgrade your plan.</span>
        </div>
      )}

      <div className="overflow-hidden rounded-[10px] border border-gray-200 text-sm">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <span className="text-gray-600">People selected</span>
          <span className="font-medium text-gray-900">
            {personCount} {personCount === 1 ? 'person' : 'people'}
          </span>
        </div>
        {estimateLoading ? (
          <div className="space-y-2 px-4 py-3">
            <div className="flex items-center gap-2">
              <Shimmer className="h-4 w-40" />
            </div>
            <div className="flex items-center gap-2">
              <Shimmer className="h-4 w-32" />
            </div>
          </div>
        ) : estimate && showCompanyBreakdown ? (
          <>
            {estimate.already_unlocked > 0 && (
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <span className="text-gray-600">Already enriched</span>
                <span className="font-medium text-gray-900">{estimate.already_unlocked} (free)</span>
              </div>
            )}
            {estimate.already_has_email > 0 && (
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <span className="text-gray-600">Existing email on file</span>
                <span className="font-medium text-gray-900">{estimate.already_has_email}</span>
              </div>
            )}
            {estimate.needs_discovery > 0 && (
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <span className="text-gray-600">Need discovery</span>
                <span className="font-medium text-gray-900">{estimate.needs_discovery}</span>
              </div>
            )}
          </>
        ) : null}
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-gray-600">Credits to use</span>
          <span className="font-medium text-gray-900">
            {estimateLoading ? <Shimmer className="h-4 w-8" /> : `up to ${totalToUnlock ?? '-'}`}
          </span>
        </div>
        {costHint && (
          <div className="border-t border-gray-200 px-4 py-2 text-[11px] text-gray-500">
            {costHint}
          </div>
        )}
      </div>

      {nothingToUnlock && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3.5">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
            <p className="text-sm text-green-800">
              All selected people have already been enriched. No additional charge.
            </p>
          </div>
        </div>
      )}

      {(estimateError || submitError) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3.5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm text-red-800">{estimateError || submitError}</p>
          </div>
        </div>
      )}

      {!(isExhausted && onUpgradeClick) && (
        <div className="flex gap-2 pt-1">
          <Button onClick={onCancel} variant="secondary" className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={estimateLoading || submitLoading || !!estimateError || !canUnlock || nothingToUnlock || balanceLoading || balanceError}
            variant="primary"
            isLoading={submitLoading}
            className="flex-[2]"
          >
            {estimateLoading
              ? 'Loading estimate...'
              : `Find decision maker emails (${totalToUnlock ?? personCount})`}
          </Button>
        </div>
      )}
      {isExhausted && onUpgradeClick && (
        <div className="flex">
          <Button onClick={onCancel} variant="secondary" className="flex-1">
            Cancel
          </Button>
        </div>
      )}
      {autoSelectedSubset && onChangeSelection && (
        <div className="text-left">
          <button
            type="button"
            onClick={onChangeSelection}
            className="text-[11px] text-[#9CA3AF] underline hover:text-gray-600 transition-colors"
          >
            Change selection
          </button>
        </div>
      )}
    </div>
  );
};

export default EnrichmentConfirmScreen;
